import { Component, computed, inject, input, resource, signal } from "@angular/core";
import { CertDNSFirewallTableComponent } from "../../../../components/cert/cert-dns-firewall-table/cert-dns-firewall-table";
import { DnsFirewallSearchFilter, DnsFirewallTypeConnection, DnsFirewallTypeEdge } from "../../../../gql/generated";
import { Sort } from "@angular/material/sort";
import { DNS_FIREWALL_QUERY } from "../../../../graphql/domain/dns-firewall";
import { Apollo } from "apollo-angular";
import { CertChartGeneric } from "../../../../components/cert/cert-chart-generic/cert-chart";
import { chartDataTransform, chartLegendTransform } from "./chart-data-transform";
import { CertFilterDns } from "../../../../components/cert/cert-filter-dns/cert-filter";

type FetchArguments = {
  filter: DnsFirewallSearchFilter;
  paginationAfter?: number;
  sort: any;
  first?: number;
};

const EMPTY_PAGE_INFO = {
  filterFields: [],
  hasNextPage: false,
  hasPreviousPage: false,
  sortFields: [],
  endCursor: 0,
  startCursor: 0,
  totalItems: 0,
};

@Component({
  selector: "dns-firewall-tab",
  templateUrl: "dns-firewall-tab.html",
  styleUrls: ["../../cert.scss"],
  imports: [CertDNSFirewallTableComponent, CertChartGeneric, CertFilterDns],
})
export class DnsFirewallTabComponent {
  private apollo = inject(Apollo);

  public prefixes = input.required<string[]>();

  public dnsFirewallResults = resource({
    request: () => ({
      filter: { ...this.searchFilter(), customerId: this.accessCustomerId },
      paginationAfter: this.paginationAfter(),
      sort: this.querySort(),
      first: this.itemsPerPage(),
    }),
    loader: async ({ request }): Promise<DnsFirewallTypeConnection> => {
      const { data } = await this.fetchDnsFirewallInformation(request);
      return data.dnsFirewallLogs;
    },
  });

  public legendResults = resource({
    request: () => ({
      filter: { ...this.searchFilter(), customerId: this.accessCustomerId },
      sort: this.querySort(),
    }),
    loader: async ({ request }): Promise<DnsFirewallTypeEdge[]> => {
      const { data } = await this.fetchDnsFirewallInformation({ ...request, first: 99999 });
      return data.dnsFirewallLogs.edges;
    },
  });
  protected loadingState = computed(() => this.dnsFirewallResults.isLoading() || this.legendResults.isLoading());

  protected searchFilter = signal<DnsFirewallSearchFilter>({});
  private itemsPerPage = signal(10);
  public pageInfo = computed(() =>
    this.dnsFirewallResults.hasValue() ?
      (this.dnsFirewallResults.value().pageInfo ?? EMPTY_PAGE_INFO)
    : EMPTY_PAGE_INFO,
  );
  public dnsFirewallLogs = computed(() =>
    this.dnsFirewallResults.hasValue() ? (this.dnsFirewallResults.value().edges ?? []) : [],
  );

  private paginationAfter = signal(0);
  private sortInfo = signal<Sort>({
    active: "recordedAt",
    direction: "desc",
  });
  private querySort = computed(() => ({
    order: [{ [this.sortInfo().active || "recordedAt"]: this.sortInfo().direction.toUpperCase() || "DESC" }],
  }));
  private accessCustomerId: string;

  protected dnsFirewallChartSeries = computed(() => chartDataTransform(this.dnsFirewallLogs()));
  protected dnsFirewallChartLegendGroups = computed(() => chartLegendTransform(this.legendResults.value()));

  constructor() {
    this.accessCustomerId = localStorage.getItem("viewingCustomerGUID");
    this.itemsPerPage.set(Number(localStorage.getItem("paginationSize") ?? 10));
  }

  async fetchDnsFirewallInformation(args: FetchArguments) {
    return this.apollo
      .use("replicatedData")
      .query<{ dnsFirewallLogs: DnsFirewallTypeConnection }>({
        query: DNS_FIREWALL_QUERY,
        fetchPolicy: "network-only",
        variables: {
          filter: args.filter,
          first: args.first ?? 10,
          after: args.paginationAfter ?? 0,
          sort: args.sort,
        },
      })
      .toPromise();
  }

  onFiltersChanged(event: DnsFirewallSearchFilter) {
    this.searchFilter.set(event);
  }

  onPaginationChanged(event: number) {
    this.paginationAfter.set(event > -1 ? event : 0);
  }

  onSortingChanged(event: Sort) {
    this.sortInfo.set(event);
  }

  onPageSizeChanged(event: any) {
    this.itemsPerPage.set(event);
  }
}
